import {PolymerElement, html} from '@polymer/polymer';
import {ScopeBinding} from './behaviors/keli-binding-behavior.js';
import {subMilliseconds, differenceInMilliseconds} from 'date-fns';
import {foregroundColorValue} from '../javascripts/modules/colors.js';

class KeliClock extends ScopeBinding(PolymerElement) {
  static get template() {
    return html`
<style>
  :host {
    display: inline;
    font-size: 1.1em;
    font-weight: 400;
    font-family: var(--keli-font-monospace);
    position: var(--keli-clock-position, static);
    top: var(--keli-clock-top, unset);
    right: var(--keli-clock-right, unset);
    color: var(--keli-clock-color, var(--keli-text));
  }
</style>

<span>[[clockValue]]</span>
`;
  }

  static get is() {
    return 'keli-clock';
  }

  static get properties() {
    return {
      keliControlTypes: {type: Array, value: ['clock']},
      checkpoints: {type: Number, observer: 'checkpointsChanged', scopevar: true},
      clockMode: {type: String, value: 'stopwatch'},
      clockValue: {type: String, value: '00:00:00'},
      color: {type: String, observer: 'colorChanged', scopevar: true},
      hours: {type: Boolean, scopevar: true},
      milliseconds: {type: Boolean, scopevar: true},
      minutes: {type: Boolean, scopevar: true},
      right: {type: Number, observer: 'rightChanged', scopevar: true},
      seconds: {type: Boolean, scopevar: true},
      start: {type: Number, observer: 'startChanged', scopevar: true},
      top: {type: Number, observer: 'topChanged', scopevar: true}
    }
  }

  constructor() {
    super();
    this.addEmitToKeli('keliClockCheckpoint');
  }

  connectedCallback() {
    super.connectedCallback();
    this.startDate = subMilliseconds(Date.now(), parseInt(this.start, 10) || 0);
    this.updateClockValue();

    this.interval = window.setInterval(() => {
      this.updateClockValue();
    }, (this.milliseconds ? 10 : 1000));

    if (this.checkpoints) {
      this.createCheckpoint();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.interval) {
      window.clearInterval(this.interval);
    }
    if (this.checkpointInterval) {
      window.clearInterval(this.checkpointInterval);
    }
  }

  createCheckpoint() {
    if (this.checkpointInterval) {
      window.clearInterval(this.checkpointInterval);
    }

    if (this.checkpoints) {
      this.checkpointInterval = window.setInterval(() => {
        this.dispatchEvent(new CustomEvent('keliClockCheckpoint', {
          detail: {
            start: this.start || 0,
            elapsed: (this.elapsed - this.start)
          }
        }));
      }, this.checkpoints || 10000);
    }
  }

  startChanged(newValue) {
    this.startDate = subMilliseconds(new Date(), parseInt(newValue, 10) || 0);
    this.updateClockValue();
  }

  checkpointsChanged() {
    this.createCheckpoint();
  }

  colorChanged(newValue) {
    this.updateStyles({'--keli-clock-color': foregroundColorValue(newValue, this)});
  }

  topChanged(newValue) {
    const value = (newValue !== undefined ? `${newValue}px` : '');
    this.updateStyles({
      '--keli-clock-position': 'absolute',
      '--keli-clock-top': value
    });
  }

  rightChanged(newValue) {
    const value = (newValue !== undefined ? `${newValue}px` : '');
    this.updateStyles({
      '--keli-clock-position': 'absolute',
      '--keli-clock-right': value
    });
  }

  updateClockValue() {
    this.elapsed = differenceInMilliseconds(Date.now(), this.startDate);
    const differenceInSeconds = Math.floor(this.elapsed / 1000);

    let newClockValues = [];
    if (this.hours !== false) {
      let hours = Math.floor((differenceInSeconds / 3600) % 24).toString();
      hours = '00'.substring(0, (2 - hours.length)) + hours;
      newClockValues.push(hours);
    }
    if (this.minutes !== false) {
      let minutes = Math.floor((differenceInSeconds / 60) % 60).toString();
      minutes = '00'.substring(0, (2 - minutes.length)) + minutes;
      newClockValues.push(minutes);
    }
    if (this.seconds !== false) {
      let seconds = Math.floor(differenceInSeconds % 60).toString();
      seconds = '00'.substring(0, (2 - seconds.length)) + seconds;
      newClockValues.push(seconds);
    }
    if (this.milliseconds) {
      let milliseconds = this.elapsed.toString().substr(-3, 3);
      milliseconds = '000'.substring(0, (3 - milliseconds.length)) + milliseconds;
      newClockValues.push(milliseconds);
    }

    this.clockValue = newClockValues.join(':');
  }
}

customElements.define(KeliClock.is, KeliClock);
export {KeliClock};
